import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

/**
 * Shared loading language for the whole app: placeholders pulse behind a soft
 * blur, and real content resolves from blurred to sharp once it is ready.
 *
 * Wrapped in `createStyles` so CSS keywords keep their literal types — a bare
 * object literal widens `'none'` to `string`, which csstype rejects.
 */
const styles = (theme: Theme) => createStyles({
  '@keyframes blurSkeletonPulse': {
    '0%': { opacity: 0.35, filter: 'blur(7px)' },
    '50%': { opacity: 0.8, filter: 'blur(2px)' },
    '100%': { opacity: 0.35, filter: 'blur(7px)' },
  },
  '@keyframes blurRevealFocus': {
    '0%': { opacity: 0.4, filter: 'blur(12px)', transform: 'scale(1.012)' },
    '100%': { opacity: 1, filter: 'blur(0px)', transform: 'none' },
  },
  '@keyframes blurSkeletonFade': {
    '0%': { opacity: 0.3 },
    '50%': { opacity: 0.75 },
    '100%': { opacity: 0.3 },
  },

  skeleton: {
    display: 'block',
    backgroundColor: theme.palette.grey3,
    backgroundImage: `linear-gradient(110deg, ${theme.palette.grey3} 0%, ${theme.palette.white} 45%, ${theme.palette.grey3} 100%)`,
    animation: '$blurSkeletonPulse 1.9s ease-in-out infinite',
    willChange: 'opacity, filter',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      opacity: 0.55,
      filter: 'none',
    },
  },
  text: {
    width: '100%',
    height: 14,
    borderRadius: 6,
    marginBottom: theme.spacing(1),
    transformOrigin: 'left center',
  },
  rect: {
    width: '100%',
    height: 72,
    borderRadius: 12,
  },
  circle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
  },

  // Dense grids animate dozens of blocks at once; animating `filter` there is
  // expensive on mobile, so those fall back to a plain opacity pulse.
  subtle: {
    animation: '$blurSkeletonFade 1.9s ease-in-out infinite',
    filter: 'none',
  },

  stack: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },

  reveal: {},
  revealLoading: {
    filter: 'blur(10px)',
    opacity: 0.45,
    pointerEvents: 'none',
    userSelect: 'none',
    willChange: 'opacity, filter',
    transition: 'filter 420ms ease, opacity 420ms ease',
    '@media (prefers-reduced-motion: reduce)': {
      filter: 'none',
      opacity: 0.6,
      transition: 'none',
    },
  },
  revealReady: {
    // No fill mode: the element must settle back to its own styles, otherwise a
    // `filter: blur(0)` lingers and keeps a containing block for fixed children.
    animation: '$blurRevealFocus 520ms cubic-bezier(0.22, 0.61, 0.36, 1)',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },

  /** Applied to interactive elements (buttons, chips) while an action is in flight. */
  pulse: {
    animation: '$blurSkeletonPulse 1.4s ease-in-out infinite',
    pointerEvents: 'none',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      opacity: 0.6,
    },
  },
});

// Injected before consumer sheets so page-level classNames can override the
// placeholder's default size without resorting to inline styles.
const useStyles = makeStyles(styles, { name: 'BlurSkeleton', index: -100 });

export default useStyles;
